import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useClipboard } from 'use-clipboard-copy';
import { CellContent, CopyableCell } from './styles';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';

interface CellWithCopyProps {
    value: any;
    displayValue: string;
}

export default function CellWithCopy({ value, displayValue }: CellWithCopyProps) {
    const { t } = useBundleTranslation();
    const clipboard = useClipboard();
    const textRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const waitForTextRender = (maxTries = 5, delay = 100) => {
        let tries = 0;

        const tryCheck = () => {
            if (!textRef.current || !wrapperRef.current) return;

            const textEl = textRef.current;
            const wrapperEl = wrapperRef.current;

            const textWidth = textEl.scrollWidth;
            const containerWidth = wrapperEl.clientWidth;

            if (textWidth > 0) {
                setShowTooltip(textWidth > containerWidth && !!displayValue);
            } else if (tries < maxTries) {
                tries++;
                setTimeout(tryCheck, delay);
            } else {
                setShowTooltip(false);
            }
        };

        tryCheck();
    };

    useEffect(() => {
        waitForTextRender();

        const wrapperEl = wrapperRef.current;
        if (!wrapperEl) return;

        const resizeObserver = new ResizeObserver(() => {
            waitForTextRender();
        });

        resizeObserver.observe(wrapperEl);

        return () => {
            resizeObserver.disconnect();
        };
    }, [displayValue]);

    const onCopy = () => {
        if (!displayValue) return;
        const copyValue = (value ?? displayValue).toString();
        clipboard.copy(copyValue);
        toast('success', t('copied'), t('copied_to_clipboard'));
    };

    const ellipsisBox = <CellContent ref={textRef}>{displayValue}</CellContent>;

    const content = showTooltip ? (
        <Tooltip followCursor placement="top-start" title={displayValue}>
            {ellipsisBox}
        </Tooltip>
    ) : (
        ellipsisBox
    );

    return (
        <CopyableCell ref={wrapperRef} onClick={onCopy}>
            {content}
        </CopyableCell>
    );
}
